import { Component, OnInit, OnDestroy, Inject, ViewContainerRef, TemplateRef } from '@angular/core';
import { BaseComponent } from '@eview/core';
import { AppState } from '@eview/core/store/states/app.state';
import { Store } from '@ngrx/store';
import { selectForms } from '@eview/core/store/selectors/forms.selector';
import { CatalogFacade } from '@eview/core/store/facades/catalog.facade';
import { Subscription, BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators
} from '@angular/forms';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { FormService } from '@eview/core/domain/form/form.service';
import { Forms, Form, FormStatusScheme } from '@eview/core/domain/form/form';
import { ToastType } from '../../toast/toast.component';
import { ToastService } from '../../toast/toast.service';
import { Router } from '@angular/router';
import { PostType } from '@eview/core/domain/post/post';
import { SearchableSelectOption } from '@eview/features/ui/searchable-select/searchable-select.component';

const PAGE_SIZE = 20;

const FORM_MODAL_OPTIONS: NgbModalOptions = {
  centered: true,
  size: 'md',
  scrollable: true,
};

@Component({
  selector: 'eview-forms-page',
  templateUrl: 'forms-page.component.html',
  styleUrls: ['forms-page.component.scss']
})
export class FormsPageComponent extends BaseComponent
  implements OnInit, OnDestroy {
  private subs: Subscription;
  tagId: number = 0;
  openModal = new BehaviorSubject(false);
  forms: Forms;
  surveyForm: UntypedFormGroup;
  isEditing: boolean = false;
  editId: number = 0;
  selectedId: number = -1;
  showDeleteConfirmation: boolean = false;
  forms$: Observable<Forms>;
  postType: string[] = [];

  get postTypeSelectOptions(): SearchableSelectOption[] {
    return this.postType.map(type => ({
      value: type.toLowerCase(),
      label: type
    }));
  }

  get statusSchemeSelectOptions(): SearchableSelectOption[] {
    return [
      { value: FormStatusScheme.Default, label: 'FORMS_STATUS_SCHEME_DEFAULT' },
      { value: FormStatusScheme.OnpeMace, label: 'FORMS_STATUS_SCHEME_ONPE' }
    ];
  }

  constructor(
    private store: Store<AppState>,
    private modalService: NgbModal,
    private formBuilder: UntypedFormBuilder,
    public toast: ToastService,
    private formService: FormService,
    private router: Router,
    @Inject(ViewContainerRef) private viewContainerRef: ViewContainerRef,
    private catalog: CatalogFacade
  ) {
    super();
    this.subs = new Subscription();
    this.forms$ = this.store.select(selectForms);
    toast.setViewContainerRef(viewContainerRef);
  }

  ngOnInit() {
    this.catalog.ensureForms();
    this.loadForms();
    this.createForm();
  }

  loadForms() {
    this.subs.add(
      this.store.select(selectForms).subscribe(forms => {
      if (!forms?.results) {
        this.forms = forms;
        return;
      }
      this.forms = {
        ...forms,
        results: forms.results.map(f => ({
          ...f,
          is_default: f.is_default ?? false,
          status_scheme: f.status_scheme || FormStatusScheme.Default
        }))
      };
    })
    );
  }

  private refreshForms(): void {
    this.catalog.ensureForms(true);
  }

  onAddFormClick(content: TemplateRef<any>) {
    this.isEditing = false;
    this.editId = 0;
    this.surveyForm.reset();
    this.modalService.open(content, FORM_MODAL_OPTIONS);
  }

  onFormEditClick(content: TemplateRef<any>, id: number) {
    this.isEditing = true;
    this.forms$.pipe(
      map((data: Forms) => data.results),
      ).subscribe((item) => {
      const form = item[id];
      if (form) {
        this.editId = form.id;
        this.surveyForm.patchValue({
            name: form.name,
            description: form.description,
            type: form.type,
            status_scheme: form.status_scheme || FormStatusScheme.Default
        });
      }
    });
    this.modalService.dismissAll();
    this.modalService.open(content, FORM_MODAL_OPTIONS);
  }

  createForm() {
    this.postType = Object.keys(PostType);
    this.surveyForm = this.formBuilder.group({
      name: ['', Validators.required],
      description: ['', [Validators.required]],
      type: ['', [Validators.required]],
      status_scheme: ['default', [Validators.required]],
    });
  }
  
  setPostTypeValidation() {
    this.surveyForm.controls['type'].setValidators([Validators.required]);
  }
  
  onSubmitClick() {
    const reqBody = this.surveyForm.value;
    if (!this.isEditing) {
      reqBody.disabled = true;
      reqBody.is_default = false;
      reqBody.status_scheme = reqBody.status_scheme || FormStatusScheme.Default;
      this.formService.createForm(reqBody).subscribe(results => {
        this.toast.show(ToastType.Success, 'TOAST_FORM_ADD_SAVED');
        this.refreshForms();
      });
    } else {
      this.formService.updateForm(this.editId, reqBody).subscribe(results => {
        this.isEditing = false;
        this.toast.show(ToastType.Success, 'TOAST_FORM_UPDATE_SAVED');
        this.refreshForms();
      });
    }
    this.surveyForm.reset();
    this.modalService.dismissAll();
  }

  /** Predeterminado: solo entre formatos habilitados del mismo tipo. */
  onDefaultFormClick(selected: Form): void {
    if (selected.disabled) {
      return;
    }
    const toUpdate: Form[] = [];
    this.forms.results.forEach(frm => {
      if (frm.id === -1 || frm.type !== selected.type) {
        return;
      }
      const next = !frm.disabled && frm.id === selected.id;
      if (frm.is_default !== next) {
        toUpdate.push({ ...frm, is_default: next });
      }
    });
    if (!toUpdate.length) {
      return;
    }
    forkJoin(toUpdate.map(f => this.formService.updateForm(f.id, f))).subscribe(() => {
      this.isEditing = false;
      this.refreshForms();
      this.toast.show(ToastType.Success, 'TOAST_FORM_UPDATE_SAVED');
    });
  }

  /** Habilitado / deshabilitado: controla si el formato aparece al crear informes. */
  onToggleEnabledClick(form: Form): void {
    const wasDisabled = form.disabled;
    const nextDisabled = !wasDisabled;
    const batch: Form[] = [];

    if (!nextDisabled) {
      const anyOtherDefault = this.forms.results.some(
        f =>
          f.id !== -1 &&
          f.type === form.type &&
          f.id !== form.id &&
          !f.disabled &&
          f.is_default
      );
      const nextIsDefault = anyOtherDefault ? !!form.is_default : true;
      batch.push({ ...form, disabled: false, is_default: nextIsDefault });
    } else {
      const clicked: Form = { ...form, disabled: true, is_default: false };
      batch.push(clicked);
      if (form.is_default) {
        const replacement = this.forms.results.find(
          f => f.id !== -1 && f.type === form.type && !f.disabled && f.id !== form.id
        );
        if (replacement) {
          batch.push({ ...replacement, is_default: true });
        }
      }
    }

    forkJoin(batch.map(f => this.formService.updateForm(f.id, f))).subscribe(() => {
      this.isEditing = false;
      this.refreshForms();
      this.toast.show(ToastType.Success, 'TOAST_FORM_UPDATE_SAVED');
    });
  }

  onCancelClick() {
    this.isEditing = false;
    this.showDeleteConfirmation = false;
    this.selectedId = -1;
    this.surveyForm.reset();
  }

  onDeleteClick(index: number) {
    this.selectedId = index;
    this.showDeleteConfirmation = true;
  }

  onAddFieldsClick(formId: number) {
    this.router.navigate(['/dashboard/post/attributes/', formId])
  }

  onDeleteConfirmationClick(formId: number) {
    if (formId) {
      this.formService.deleteForm(formId).subscribe(() => {
        this.selectedId = -1;
        this.refreshForms();
        this.toast.show(ToastType.Success, 'TOAST_FORM_DELETED');
      });
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
